import {
  useMfaOtpChallenge,
  useScreen,
} from "@auth0/auth0-acul-react/mfa-otp-challenge";
import type {
  ContinueOptions,
  ScreenMembersOnMfaOtpChallenge,
} from "@auth0/auth0-acul-react/types";

import { executeSafely } from "@/utils/helpers/executeSafely";

import locales from "../locales/en.json";

export const useMfaOtpChallengeManager = () => {
  const screen = useScreen();
  const mfaOtpChallenge = useMfaOtpChallenge();

  const { texts, data } = screen;

  const handleSubmitOtp = async (code: string): Promise<void> => {
    const options: ContinueOptions = {
      code: code?.trim() || "",
      // ⚠️ ALWAYS false. NEVER wire this to a checkbox on this screen.
      //
      // Sent explicitly rather than omitted so the intent is visible in the
      // payload and a future refactor cannot reintroduce it by accident.
      rememberDevice: false,
    };

    // The code is deliberately NOT interpolated into the log line the way
    // email-identifier-challenge interpolates its options — that screen's
    // payload is an email code already sitting in the member's inbox, this
    // one is a live second factor.
    await executeSafely("Submit MFA OTP challenge", () =>
      mfaOtpChallenge.continue(options)
    );
  };

  const handleTryAnotherMethod = async (): Promise<void> => {
    await executeSafely("Try another MFA method", () =>
      mfaOtpChallenge.tryAnotherMethod({})
    );
  };

  return {
    mfaOtpChallenge,
    handleSubmitOtp,
    handleTryAnotherMethod,
    texts: (texts || {}) as ScreenMembersOnMfaOtpChallenge["texts"],
    // ⚠️ "REMEMBER THIS DEVICE FOR 30 DAYS" IS SUPPRESSED ON THIS SCREEN.
    //
    // Auth0 DOES offer it here — `screen.data.showRememberDevice` was observed
    // true on a live Flow B step-up (2026-09-12), which contradicts the
    // original assumption in this file that `challengeWithAny()` would leave
    // it off. The assumption was wrong; this comment replaces it.
    //
    // It is suppressed because it directly contradicts what this screen is
    // for. The gate exists to prove the member authenticated SECONDS ago. A
    // remembered browser means a later step-up can satisfy MFA WITHOUT
    // challenging the factor, while `max_age` still moves `auth_time` — so
    // `acr` and `auth_time` would both look satisfied with no fresh challenge
    // behind them. That is the same fail-open shape ruled out on the silent-
    // renewal path, reintroduced through a checkbox.
    //
    // Suppressed UNCONDITIONALLY rather than only on the step-up path because
    // the screen cannot tell step-up from ordinary login (no acr_values in the
    // ACUL context — see index.tsx). Losing the convenience on Flow B's login
    // MFA is the lesser cost, and this tenant has no remember-browser policy
    // configured anyway.
    //
    // The raw flag stays exposed for diagnostics — it is what a future reader
    // should check before concluding Auth0 has stopped offering it.
    auth0OffersRememberDevice: data?.showRememberDevice === true,
    data,
    locales,
  };
};
